/**
 * Contenu editorial : point d'entree unique.
 *
 * Une langue traduite = un fichier dans `content/<lang>/` et une entree dans
 * `CONTENT` ci-dessous. Tout le reste en decoule : les URL prerendues, les
 * `hreflang`, le sitemap et les regles de cache lisent cette meme liste.
 *
 * UNE LANGUE ABSENTE N'EST PAS UNE PAGE VIDE : l'ecran retombe sur le francais.
 * Simplement, son URL prefixee n'existe pas et personne ne la lui promet, ce
 * qui est exactement le comportement voulu : declarer une alternative qui
 * renvoie 404 fait ignorer TOUTES les declarations de la page.
 */
import { DEFAULT_LANGUAGE, type Language } from '../locales/index.js';
import type { LanguageContent } from './registry.js';
import { FR } from './fr/index.js';
import { EN } from './en/index.js';
import { ES } from './es/index.js';
import { PT } from './pt/index.js';
import { DE } from './de/index.js';
import { IT } from './it/index.js';
import { NL } from './nl/index.js';
import { PL } from './pl/index.js';
import { RO } from './ro/index.js';
import { CS } from './cs/index.js';
import { HU } from './hu/index.js';
import { TR } from './tr/index.js';
import { RU } from './ru/index.js';
import { VI } from './vi/index.js';
import { ID } from './id/index.js';
import { JA } from './ja/index.js';
import { KO } from './ko/index.js';

export type { Article, Block, Page, Section } from './types.js';
export type { FaqEntry, ModeCard, Step } from './fr/home.js';
export type { ModeRow } from './fr/modes.js';
export type { LanguageContent } from './registry.js';
export { CONTENT_PATHS, localePrefix } from './registry.js';
import { localePrefix as localePrefixOf } from './registry.js';
export { pageMeta, PAGE_METAS, translatedLanguages } from './meta.js';
export type { PageMeta } from './meta.js';

const CONTENT: Partial<Record<Language, LanguageContent>> = {
  fr: FR,
  en: EN,
  es: ES,
  pt: PT,
  de: DE,
  it: IT,
  nl: NL,
  pl: PL,
  ro: RO,
  cs: CS,
  hu: HU,
  tr: TR,
  ru: RU,
  vi: VI,
  id: ID,
  ja: JA,
  ko: KO,
};

/** Le contenu d'une langue, ou le francais a defaut. */
export function contentFor(language: Language): LanguageContent {
  return CONTENT[language] ?? FR;
}

/** Cette langue a-t-elle une VRAIE traduction, et donc ses propres URL ? */
export function isTranslated(language: Language): boolean {
  return CONTENT[language] !== undefined;
}

/**
 * Prefixe des liens vers les pages de CONTENU.
 *
 * Vide tant que la langue n'est pas traduite, et c'est le point important :
 * `/es/how-to-play` afficherait du francais sous une adresse espagnole. Une
 * adresse doit correspondre a ce qu'elle sert, sans quoi on la partage, on la
 * met en favori, un moteur l'explore, et elle promet une traduction qui
 * n'existe pas.
 *
 * A ne pas confondre avec `localePrefix`, qui vaut pour l'ACCUEIL : celui-ci
 * est traduit dans les dix-sept langues, son prefixe est donc toujours legitime.
 */
export function contentPrefix(language: Language): string {
  return isTranslated(language) ? localePrefixOf(language) : '';
}

export function articleBySlug(language: Language, slug: string) {
  return contentFor(language).blog.find((article) => article.slug === slug) ?? null;
}

export { DEFAULT_LANGUAGE };
